import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight, Cpu, Microscope, Dna, Globe } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const stats = [
  { value: '3+', label: 'Active Projects' },
  { value: 'India', label: 'Based in India' },
  { value: '2025', label: 'Founded' },
  { value: '2', label: 'Core Innovations' },
];

const pillars = [
  { icon: Dna, title: 'Bio-Integration', desc: 'Seamlessly merging biological systems with engineered interfaces.' },
  { icon: Cpu, title: 'Edge Intelligence', desc: 'AI that runs at the speed of thought, not the speed of servers.' },
  { icon: Microscope, title: 'Nano-Precision', desc: 'Molecular-scale engineering for therapeutic and diagnostic use.' },
  { icon: Globe, title: 'Global Impact', desc: 'Building solutions accessible across healthcare systems worldwide.' },
];

export default function HomePage() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/projects`).then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <main>
      {/* ── Hero ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1660846125563-a07c7ceee60b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-bvx-bg/60 via-bvx-bg/80 to-bvx-bg" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 80% 60% at 20% 50%, rgba(0,240,255,0.1) 0%, transparent 70%)' }}
        />

        <div className="relative z-10 px-6 md:px-12 lg:px-24 pt-28 max-w-7xl w-full">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <span className="section-label" data-testid="hero-label">India-Based BioTech Innovation</span>
          </motion.div>

          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="font-outfit font-bold text-5xl sm:text-6xl lg:text-7xl text-white mt-6 mb-6 leading-tight"
            data-testid="hero-headline"
          >
            Engineering the<br />
            Future of{' '}
            <span className="text-gradient-cyan">Human Biology</span>
          </motion.h1>

          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-white/55 text-base sm:text-lg max-w-2xl font-inter-tight leading-relaxed"
          >
            We build at the intersection of biotech and advanced technology — from
            neural interfaces to molecular nanotechnology that redefines human potential.
          </motion.p>

          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={3}
            className="flex flex-wrap gap-4 mt-10"
          >
            <Link to="/projects" className="btn-primary flex items-center gap-2" data-testid="hero-explore-btn">
              Explore Projects <ArrowRight size={15} />
            </Link>
            <Link to="/contact" className="btn-outline" data-testid="hero-contact-btn">
              Get In Touch
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="px-6 md:px-12 lg:px-24 py-16 border-y border-bvx-border">
        <div className="max-w-7xl grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              variants={fadeUp} initial="hidden" whileInView="visible" custom={i} viewport={{ once: true }}
              className="text-left"
              data-testid={`stat-${i}`}
            >
              <p className="font-outfit font-bold text-4xl text-bvx-cyan">{s.value}</p>
              <p className="font-inter-tight text-white/50 text-sm mt-1">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── About Brief ── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <span className="section-label">About Biovex</span>
            <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-6 mb-6 leading-tight">
              Where Biology Meets<br />
              <span className="text-gradient-cyan">Engineering</span>
            </h2>
            <p className="text-white/55 font-inter-tight text-base leading-relaxed mb-4">
              Biovex Technologies is an India-based startup pioneering the next generation of human augmentation
              technologies. We combine cutting-edge research in molecular biology, AI, and materials science.
            </p>
            <p className="text-white/55 font-inter-tight text-base leading-relaxed mb-8">
              Our mission is to build technologies that extend human potential — making people healthier,
              more capable, and more connected to their own biology than ever before.
            </p>
            <Link to="/about" className="flex items-center gap-2 text-bvx-cyan text-sm font-medium hover:gap-3 transition-all duration-200" data-testid="about-learn-more">
              Learn our story <ChevronRight size={16} />
            </Link>
          </motion.div>

          <div className="grid grid-cols-2 gap-4">
            {pillars.map((p, i) => (
              <motion.div
                key={p.title}
                variants={fadeUp} initial="hidden" whileInView="visible" custom={i * 0.5} viewport={{ once: true }}
                className="glass-card p-6 card-hover"
                data-testid={`pillar-card-${i}`}
              >
                <div className="w-10 h-10 rounded-lg bg-bvx-cyan/10 border border-bvx-cyan/20 flex items-center justify-center mb-4">
                  <p.icon size={18} className="text-bvx-cyan" />
                </div>
                <h3 className="font-outfit font-semibold text-white text-base mb-2">{p.title}</h3>
                <p className="text-white/45 text-xs font-inter-tight leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Projects (Bento Grid) ── */}
      {projects.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-bvx-border">
          <div className="max-w-7xl">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
              <span className="section-label">Our Innovations</span>
              <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white mt-6 leading-tight">
                Products & Projects
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {projects.slice(0, 2).map((project, i) => (
                <motion.div
                  key={project.id}
                  variants={fadeUp} initial="hidden" whileInView="visible" custom={i} viewport={{ once: true }}
                  className={`glass-card overflow-hidden card-hover ${i === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
                  data-testid={`project-card-${i}`}
                >
                  {project.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={project.image_url}
                        alt={project.name}
                        className="w-full h-full object-cover opacity-70"
                      />
                    </div>
                  )}
                  <div className="p-7">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`status-badge status-${project.status}`}>{project.status}</span>
                    </div>
                    <h3 className="font-outfit font-bold text-white text-2xl mb-2">{project.name}</h3>
                    <p className="text-bvx-cyan text-sm font-inter-tight mb-3">{project.tagline}</p>
                    <p className="text-white/50 text-sm font-inter-tight leading-relaxed line-clamp-3">
                      {project.description.split('\n')[0]}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4 mb-5">
                      {project.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="text-xs text-white/40 bg-white/5 border border-white/8 px-3 py-1 rounded-full font-inter-tight">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <Link
                      to="/projects"
                      className="flex items-center gap-2 text-bvx-cyan text-sm font-medium hover:gap-3 transition-all duration-200"
                      data-testid={`project-view-${i}`}
                    >
                      View Details <ChevronRight size={14} />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-10 text-left">
              <Link to="/projects" className="btn-outline inline-flex items-center gap-2" data-testid="view-all-projects">
                View All Projects <ArrowRight size={15} />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* ── Mission Statement ── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="glass-card p-10 md:p-16 relative overflow-hidden"
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(57,255,20,0.05) 0%, transparent 70%)' }}
            />
            <span className="section-label mb-8 block">Our Mission</span>
            <blockquote className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-4xl relative z-10">
              "To build technologies that{' '}
              <span className="text-gradient-green">extend human potential</span>
              {' '}— making people healthier, more capable, and more connected to their bodies than ever before."
            </blockquote>
            <p className="mt-8 text-white/40 text-sm font-inter-tight">— Biovex Technologies Mission Statement</p>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="px-6 md:px-12 lg:px-24 py-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <span className="section-label">Work With Us</span>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-end justify-between gap-8">
              <h2 className="font-outfit font-bold text-4xl sm:text-5xl text-white leading-tight max-w-xl">
                Ready to collaborate<br />on the{' '}
                <span className="text-gradient-cyan">future?</span>
              </h2>
              <div className="flex flex-col sm:flex-row gap-4 shrink-0">
                <Link to="/contact" className="btn-primary inline-flex items-center gap-2 text-center justify-center" data-testid="cta-contact-btn">
                  Get In Touch <ArrowRight size={15} />
                </Link>
                <Link to="/research" className="btn-outline inline-flex items-center gap-2 text-center justify-center" data-testid="cta-research-btn">
                  Read Research
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
